// ISO week grid (Mon–Sun) for the Calendar view — thead day-of-week headers,
// tbody with one row per calendar week (CalCell × 7 + WeekAvgCell), plus a
// trailing WeekdayAvgRow (same-weekday average across the whole period). The
// "Week avg." column has no header of its own — WeekAvgCell already labels
// itself per row, so a repeated column title would be redundant. Wrapped in
// its own horizontal-scroll container so a wide grid never forces the whole
// page to scroll sideways.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import useMirrorScrollbar from "../../../../../hooks/useMirrorScrollbar";
import { ISO_WEEKDAYS, weekdayLabel, CalCell, WeekAvgCell, WeekdayAvgRow, dayShape } from "./CalCell";

export const CalendarGrid = ({
  weekRows,
  planStartDates,
  firstUtcDate,
  weekdayAvgs,
  overallAvg,
  onDayClick,
}) => {
  const { i18n } = useTranslation("athlete-portal");
  // weekdayLabel reads moment's active locale — recompute only when the
  // language actually changes, not on every unrelated re-render.
  const weekdayLabels = useMemo(
    () => ISO_WEEKDAYS.map(weekdayLabel),
    [i18n.language]
  );

  // Mirror scrollbar: the real grid can be taller than the viewport, so its
  // native horizontal scrollbar (at the very bottom of the table) can end up
  // far below the fold. See useMirrorScrollbar.js for how the sync + the
  // needsScroll gating work. The real grid's own scrollbar is hidden in CSS
  // (np-cal-grid-scroll) so only the mirror strip is ever visible.
  const {
    scrollRef: gridScrollRef,
    mirrorRef: mirrorScrollRef,
    needsScroll,
    scrollWidth,
    onScrollRef,
    onScrollMirror,
  } = useMirrorScrollbar([weekRows]);

  return (
    <div>
      <div
        className="np-cal-grid-scroll"
        ref={gridScrollRef}
        onScroll={onScrollRef}
      >
        <table className="np-cal-grid-table">
          <thead>
            <tr>
              {ISO_WEEKDAYS.map((wd, i) => (
                <th key={wd} className="np-cal-grid-head">
                  {weekdayLabels[i]}
                </th>
              ))}
              <th className="np-cal-grid-head" />
            </tr>
          </thead>
          <tbody>
            {weekRows.map(({ monday, cells, avg, spansPlans, firstCellIndex }) => (
              <tr key={monday}>
                {cells.map((day, i) => (
                  <CalCell
                    key={ISO_WEEKDAYS[i]}
                    day={day}
                    planStartsHere={!day.outOfRange && planStartDates.has(day.utcDate)}
                    isFirstDay={!day.outOfRange && day.utcDate === firstUtcDate}
                    isRowStart={i === firstCellIndex}
                    onDayClick={onDayClick}
                  />
                ))}
                <WeekAvgCell avg={avg} spansPlans={spansPlans} />
              </tr>
            ))}
            <WeekdayAvgRow avgs={weekdayAvgs} overallAvg={overallAvg} />
          </tbody>
        </table>
      </div>

      {needsScroll && (
        <div
          className="np-cal-grid-mirror-scroll"
          ref={mirrorScrollRef}
          onScroll={onScrollMirror}
        >
          {/* Spacer — width comes from useMirrorScrollbar's live scrollWidth
              measurement, so this mirror's scroll range always matches the
              real grid's exactly (no hand-kept-in-sync CSS width). */}
          <div className="np-cal-grid-mirror-scroll__spacer" style={{ width: scrollWidth }} />
        </div>
      )}
    </div>
  );
};

const weekRowShape = PropTypes.shape({
  monday: PropTypes.number,
  cells: PropTypes.arrayOf(dayShape),
  avg: PropTypes.object,
  spansPlans: PropTypes.number,
  firstCellIndex: PropTypes.number,
});

CalendarGrid.propTypes = {
  weekRows: PropTypes.arrayOf(weekRowShape).isRequired,
  planStartDates: PropTypes.instanceOf(Set).isRequired,
  firstUtcDate: PropTypes.number.isRequired,
  weekdayAvgs: PropTypes.arrayOf(PropTypes.object).isRequired,
  overallAvg: PropTypes.object.isRequired,
  onDayClick: PropTypes.func,
};

export default CalendarGrid;
